import React, { useState, useRef } from 'react';
import clsx from 'clsx';

import styles from './DropDown.module.scss';

export type PopoverProps = {
  content: React.ReactNode;
  placement: 'bottom' | 'bottomRight' | 'leftTop';
  className?: string;
};

const MARGIN_FROM_HOVER_ELEMENT = 10;
const transitionDuration = 400;

export const DropDown: React.FC<PopoverProps> = ({ children, content, placement, className }) => {
  const element = useRef<HTMLDivElement | null>(null);
  const [renderAllowed, setRenderAllowed] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const disallowRenderTimeoutId = useRef<NodeJS.Timeout>();

  const showContent = () => {
    if (disallowRenderTimeoutId.current) clearTimeout(disallowRenderTimeoutId.current);
    if (fadingOut) setFadingOut(false);
    setRenderAllowed(true);
  };

  const hideContent = () => {
    setFadingOut(true);
    disallowRenderTimeoutId.current = setTimeout(() => {
      setRenderAllowed(false);
      setFadingOut(false);
    }, transitionDuration);
  };

  const getPosition = () => {
    if (!element.current) return undefined;
    const parentWidth = element.current.parentElement?.getBoundingClientRect().width || 0;

    switch (placement) {
      case 'bottom':
        return {
          transform: 'translateX(-50%)',
          left: '50%',
          top: element.current.offsetHeight - MARGIN_FROM_HOVER_ELEMENT,
        };
      case 'bottomRight':
        return {
          right: -MARGIN_FROM_HOVER_ELEMENT,
          top: element.current.offsetHeight - MARGIN_FROM_HOVER_ELEMENT,
        };
      case 'leftTop':
        return {
          left: parentWidth - MARGIN_FROM_HOVER_ELEMENT * 2,
          top: -element.current.offsetHeight / 2,
        };
      default:
        return {
          left: 0,
          top: 0,
        };
    }
  };

  return (
    <div className={clsx(styles.root, className)} onMouseEnter={showContent} onMouseLeave={hideContent} ref={element}>
      {children}
      {renderAllowed && (
        <div className={clsx(styles.wrapper, { [styles.fadingOut]: fadingOut })} style={getPosition()}>
          <div className={styles.body}>{content}</div>
        </div>
      )}
    </div>
  );
};
